import React from 'react';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './Seam.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * Arrow keys move a seam a pixel at a time; Shift makes it ten.
 *
 * A mouse-only resize is not an option — the seam is one CSS pixel wide, which is the hardest
 * target in the whole layout, and the people most likely to miss it are the ones least able to
 * chase it.
 */
const NUDGE_PX = 1;
const NUDGE_COARSE_PX = 10;

export interface SeamProps {
  /**
   * Accessible name. A separator with no name is read out as an unlabelled slider, so this says
   * which pane the seam sizes, e.g. "Agent pane width".
   */
  label: string;
  /** Live width of the pane this seam sizes, in px. */
  value: number;
  min: number;
  max: number;
  /** True while this seam owns the pointer. Lights it exactly as hover does. */
  dragging?: boolean;
  onDragStart?: (event: React.PointerEvent<HTMLDivElement>) => void;
  /** Signed pixels. Left is negative. */
  onNudge?: (delta: number) => void;
  theme?: HeaderTheme;
  /** Stories and visual tests only: paints the lit state without a real pointer. */
  forceHover?: boolean;
  className?: string;
}

/**
 * The 1px rule between two panes, which is also the handle that resizes them.
 *
 * There is no grip, no dots, no pill and no widened bar on hover — LB9's caption is explicit that
 * "the 1px rule IS the handle", and the whole affordance is the line brightening from
 * `--hv15-seam` to `--hv15-seam-active` under an 8px hit area that the layout cannot see.
 *
 * It is a real element rather than a `border-right` on the pane before it (D1) precisely because it
 * has to carry that hit area and that colour as one object; a border can carry neither, and the
 * pane widths then stop matching the numbers the designer wrote inside the artboard.
 */
export function Seam({
  label,
  value,
  min,
  max,
  dragging = false,
  onDragStart,
  onNudge,
  theme: themeProp,
  forceHover = false,
  className,
}: SeamProps) {
  const theme = useV15Theme(themeProp);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    // Otherwise the arrow scrolls the pane the seam is supposed to be resizing.
    event.preventDefault();
    const step = event.shiftKey ? NUDGE_COARSE_PX : NUDGE_PX;
    onNudge?.(event.key === 'ArrowLeft' ? -step : step);
  };

  /*
   * A focusable `separator` is ARIA's window-splitter: once it carries `aria-valuenow` the role
   * stops being a structural rule and becomes a widget, and the arrow keys are a real alternative
   * to the drag rather than a courtesy.
   *
   * Both rules disabled here read `separator` as purely structural, so they object to a focusable
   * one and to the key handler that makes it worth focusing. The splitter is the exception they do
   * not model — a resize that only a mouse can perform is not shippable.
   */
  /* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      data-dragging={dragging}
      className={cn(themes[theme], styles.seam, forceHover && styles.forceHover, className)}
      onPointerDown={onDragStart}
      onKeyDown={onKeyDown}
    />
  );
  /* eslint-enable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */
}
